import {
  Editor,
  OverflowingToolbar,
  TLCursorType,
  useDefaultHelpers,
  useEditor,
  useValue,
} from "tldraw"
import { Toolbar, ToolbarIconButton } from "./Toolbar"
import {
  MousePointer,
  Pencil,
  Hand,
  Type,
  PaintBucket,
  MoveUpRight,
  Image,
  Pickaxe,
  Highlighter,
  Eraser,
  Square,
  Circle,
} from "lucide-react"
import { useCurrentLayer } from "../hooks/useCurrentLayer"
import { StrokeWidthNum } from "./LayerButton"
import { Expr } from "../../shared/Expr"
import { useCurrentPage } from "../hooks/useCurrentPage"
import { ToolbarButton } from "./ToolbarButton"
import { ToolbarDivider } from "./ToolbarDivider"
import { FillPencil } from "../icons/FillPencil"
import { useState, useRef, useEffect } from "react"
import Slider from "@mui/material/Slider"
import { IonGrid, IonItem, IonList, IonRow } from "@ionic/react"
import { MarkerDrawing } from "../tools/MarkerDrawing"
import { GeoShapeGeoStyle } from "../../shared/LDGeoShape"
import { set } from "lodash"
import { LDPage } from "../../shared/LDPage"
import { LayerProps } from "../../shared/Layer"
import Checkbox from "@mui/material/Checkbox"
import { RoundedToolbar } from "./RoundedToolbar"
import { IconButton, Tooltip } from "@mui/material"
import { Nudge } from "../icons/Nudge"
import { ToolButton } from "./ToolButton"
import { LDShape } from "../../shared/LDShape"
import { LDInstanceMeta } from "../../shared/LDInstance"
import { on } from "events"
import { CursorType, setCursor } from "../components/CustomCursor"

type Props = {
  onLayerSettingsClick: (ev: React.MouseEvent) => void
  onOnlySelectedLayerChange: (check: boolean) => void
}

export function ToolsToolbar({
  onLayerSettingsClick,
  onOnlySelectedLayerChange,
}: Props) {
  const editor = useEditor()
  const page = useCurrentPage()
  const helpers = useDefaultHelpers()
  const currentLayer = useCurrentLayer()
  const currentTool = useValue("currentTool", () => editor.getCurrentTool(), [
    editor,
  ])
  const zoomLevel = useValue("zoomLevel", () => editor.getZoomLevel(), [editor])

  useEffect(() => {
    if (currentTool.id === "eraser2" || currentTool.id === "marker") {
      // onChangeCursorRadius(
      //   editor,
      //   currentTool.id == "eraser2"
      //     ? (page.meta.eraserRadius ?? 10)
      //     : (page.meta.layers[currentLayer.id]?.currentMarkerRadius ?? 20) *
      //         0.8,
      // )
    } else {
      setCursor(editor, currentTool.id as CursorType)
    }
  }, [currentTool, editor, zoomLevel])

  const [geoPopoverOpen, setGeoPopoverOpen] = useState(false)

  const instanceMeta = useValue(
    "instance meta",
    () => editor.getInstanceState().meta as LDInstanceMeta,
    [editor],
  ) as LDInstanceMeta

  const eraserRadius = instanceMeta.eraserRadius
  const onlySelectedLayer = instanceMeta.onlySelectedLayer
  const popoverRef = useRef<HTMLDivElement>(null)

  function handleMarkerRadiusChange(value: number) {
    const r = value
    //onChangeCursorRadius(editor, r * 0.8)
    editor.updatePage({
      id: page.id,
      meta: {
        ...page.meta,
        layers: {
          ...page.meta.layers,
          [currentLayer.id]: {
            ...currentLayer,
            currentMarkerRadius: r,
          },
        },
      },
    })

    // setToolProp("marker", "radius", r)
    //editor.setProp("markerRadius", r)
  }
  const handleEraserRadiusChange = (value: number) => {
    const r = value
    // onChangeCursorRadius(editor, r)

    editor.updateInstanceState({
      meta: {
        ...instanceMeta,
        eraserRadius: r,
      },
    })

    // setToolProp("eraser2", "radius", r)
    //editor.setProp("eraserRadius", r)
  }

  function handleOnlySelectedLayerChange(check: boolean) {
    editor.updateInstanceState({
      meta: { ...instanceMeta, onlySelectedLayer: check },
    })
    onOnlySelectedLayerChange(check)
    const nonLayerShapes = editor
      .getCurrentPageShapes()
      .filter(
        (shape) =>
          LDShape.getLayerId(shape as LDShape) !== page.meta.currentLayer &&
          shape.parentId === page.id,
      )
      .map((shape) => ({ ...shape, isLocked: check }))
    editor.updateShapes(nonLayerShapes)
  }

  const geoStyle = useValue(
    "geoStyle",
    () => editor.getStyleForNextShape(GeoShapeGeoStyle),
    [editor],
  )

  return (
    <div style={{ position: "relative", display: "flex" }}>
      <div
        style={{
          position: "absolute",
          top: -34,
          display: "flex",
          width: 210,
        }}
      >
        {(currentTool.id === "eraser2" ||
          currentTool.id === "select" ||
          currentTool.id === "nudge") && (
          <div style={{ flex: 2 }}>
            <Checkbox
              size="small"
              style={{ paddingRight: 2 }}
              checked={onlySelectedLayer}
              onChange={(e) => handleOnlySelectedLayerChange(e.target.checked)}
            />
            Only current layer
          </div>
        )}

        {currentTool.id === "eraser2" && (
          <Slider
            size="small"
            valueLabelDisplay="auto"
            ref={popoverRef}
            value={eraserRadius}
            min={2}
            max={30}
            step={1}
            onChange={(_, value) => handleEraserRadiusChange(value as number)}
            aria-labelledby="marker-radius-slider"
            style={{ flex: 1 }}
          />
        )}
      </div>

      <RoundedToolbar direction="horizontal">
        <ToolButton
          icon="hand"
          tooltip="Pan"
          tooltipPlacement="top"
          selected={currentTool.id == "hand"}
          onClick={() => {
            editor.setCursor({ type: "grab", rotation: 0 })
            setCursor(editor, "hand")
            editor.setCurrentTool("hand")
          }}
        />
        <ToolButton
          tooltip="Select"
          tooltipPlacement="top"
          icon="mouse-pointer"
          selected={currentTool.id == "select"}
          // selected={currentTool.id == "select"}
          onClick={() => {
            handleOnlySelectedLayerChange(instanceMeta.onlySelectedLayer)
            editor.setCursor({ type: "default", rotation: 0 })
            setCursor(editor, "select", "select")
            editor.setCurrentTool("select")
          }}
        />
        <ToolButton
          icon="nudge"
          tooltip="Nudge"
          tooltipPlacement="top"
          selected={currentTool.id == "nudge"}
          onClick={() => {
            handleOnlySelectedLayerChange(instanceMeta.onlySelectedLayer)
            editor.setCurrentTool("nudge")
          }}
        />
        <ToolButton
          icon="eraser"
          tooltip="Eraser"
          tooltipPlacement="top"
          selected={currentTool.id == "eraser2"}
          onClick={() => {
            handleOnlySelectedLayerChange(instanceMeta.onlySelectedLayer)
            setCursor(editor, "circle", "eraser2")
            editor.setCurrentTool("eraser2")
          }}
        />
        <ToolButton
          icon="image"
          tooltip="Insert Image"
          tooltipPlacement="top"
          selected={currentTool.id == "asset"}
          onClick={() => helpers.insertMedia()}
        />
      </RoundedToolbar>

      {/* <Toolbar direction="horizontal" padded={false}>
        <ToolbarButton title="Layer Settings" onClick={onLayerSettingsClick}>
          <div style={{ position: "relative", transform: "scale(.75)" }}></div>
          {currentLayer.name}
        </ToolbarButton>

        {true && (
          <>
            <div style={{ position: "relative" }}>
              {currentTool.id === "marker" && (
                <div style={{ position: "absolute", top: -34 }}>
                  <Slider
                    size="small"
                    valueLabelDisplay="auto"
                    ref={popoverRef}
                    value={currentMarkerRadius}
                    min={2}
                    max={30}
                    step={1}
                    onChange={(e, value) =>
                      handleMarkerRadiusChange(value as number)
                    }
                    aria-labelledby="marker-radius-slider"
                    style={{ width: "100px" }}
                  />
                </div>
              )}

              <ToolbarIconButton
                tooltip="Marker"
                icon={Highlighter}
                selected={currentTool.id === "marker"}
                onClick={(e) => {
                  setCursor(editor, "circle", "marker")
                  editor.setCursor({
                    type: "none",
                    rotation: 0,
                  })
                  editor.setCurrentTool("marker")
                }}
              />
            </div>
            <ToolbarIconButton
              tooltip="Draw Fill"
              icon={FillPencil}
              selected={currentTool.id == "draw-fill"}
              onClick={() => editor.setCurrentTool("draw-fill")}
            />

            <ToolbarIconButton
              tooltip="Bucket Fill"
              icon={PaintBucket}
              selected={currentTool.id == "bucket"}
              onClick={() => editor.setCurrentTool("bucket")}
            />
          </>
        )}

        <>
          <ToolbarIconButton
            tooltip="Pencil"
            icon={Pencil}
            selected={currentTool.id == "custom-draw"}
            onClick={() => editor.setCurrentTool("custom-draw")}
          />
          <ToolbarIconButton
            tooltip="Arrow"
            icon={MoveUpRight}
            selected={currentTool.id == "arrow2"}
            onClick={() => editor.setCurrentTool("arrow2")}
          />
          <ToolbarIconButton
            tooltip="Text"
            icon={Type}
            selected={currentTool.id == "text2"}
            onClick={() => editor.setCurrentTool("text2")}
          />
          <div
            style={{ position: "relative" }}
            onMouseEnter={() => setGeoPopoverOpen(true)}
            onMouseLeave={() => setGeoPopoverOpen(false)}
          >
            {geoPopoverOpen && (
              <div
                style={{ position: "absolute", top: -134, paddingBottom: 15 }}
              >
                <ToolbarIconButton
                  tooltip=""
                  icon={Square}
                  selected={geoStyle === "rectangle"}
                  onClick={() => {
                    editor.setStyleForNextShapes(GeoShapeGeoStyle, "rectangle")
                    editor.setCurrentTool("custom-geo")
                  }}
                />
                <ToolbarIconButton
                  tooltip=""
                  icon={Circle}
                  selected={geoStyle === "ellipse"}
                  onClick={() => {
                    editor.setStyleForNextShapes(GeoShapeGeoStyle, "ellipse")
                    editor.setCurrentTool("custom-geo")
                  }}
                />
                <ToolbarIconButton
                  tooltip=""
                  icon={"tool-line"}
                  selected={geoStyle === "cloud"}
                  onClick={() => {
                    editor.setStyleForNextShapes(GeoShapeGeoStyle, "cloud")
                    editor.setCurrentTool("custom-line")
                  }}
                />
              </div>
            )}
            <ToolbarIconButton
              tooltip=""
              icon={
                geoStyle === "rectangle"
                  ? Square
                  : geoStyle === "ellipse"
                    ? Circle
                    : "tool-line"
              }
              selected={
                currentTool.id === "custom-geo" ||
                currentTool.id === "custom-line"
              }
              onClick={() => {
                if (editor.getStyleForNextShape(GeoShapeGeoStyle) === "cloud") {
                  editor.setCurrentTool("custom-line")
                } else {
                  editor.setCurrentTool("custom-geo")
                }
              }}
              // onHoverStart={() => setGeoPopoverOpen(true)}
              // onHoverEnd={() => setGeoPopoverOpen(false)}
            />
          </div>
        </>
      </Toolbar>
      */}
    </div>
  )
}
